// =============================================================================
// PAGES/ADMINPARAMETROSINSTITUICAO.TSX - SPEC_BLOCO3 (14/07/2026)
// =============================================================================
// Tela mínima (D5): lista de parâmetros de INSTITUIÇÃO vigentes (régua de
// validade da aprovação de crédito, por banco) + form de nova vigência. Sem
// edição inline - mesmo padrão de AdminParametrosNormativos.tsx (append-only).
// =============================================================================

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Building2, Save } from 'lucide-react';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { parametrosInstituicaoApi, type ParametroInstituicao } from '../services/api';

const BANCOS = ['CAIXA', 'ITAU', 'SANTANDER', 'BRADESCO', 'INTER', 'OUTRO'] as const;

// Estilo de campo no padrão "Novo Vistoriador" (mesmo do drawer de parte).
const INPUT_CLS =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 ' +
  'placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-otm-navy/30 ' +
  'focus:border-otm-navy transition-colors';
const LABEL_CLS = 'block text-sm font-medium text-slate-700 mb-1';

export default function AdminParametrosInstituicao() {
  const [parametros, setParametros] = useState<ParametroInstituicao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [historicoDe, setHistoricoDe] = useState<{ banco: string; chave: string } | null>(null);
  const [historico, setHistorico] = useState<ParametroInstituicao[]>([]);

  const [formAberto, setFormAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({
    banco: 'CAIXA' as (typeof BANCOS)[number],
    chave: 'VALIDADE_APROVACAO_DIAS',
    valor: '',
    fonte: '',
  });

  async function carregar() {
    setCarregando(true);
    setErro('');
    try {
      const dados = await parametrosInstituicaoApi.listar();
      setParametros(dados);
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar parâmetros.');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => { carregar(); }, []);

  async function abrirHistorico(banco: string, chave: string) {
    setHistoricoDe({ banco, chave });
    try {
      const dados = await parametrosInstituicaoApi.historico(banco, chave);
      setHistorico(dados);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Falha ao carregar histórico.');
    }
  }

  async function salvarNovaVigencia() {
    if (!form.chave.trim() || !form.valor.trim() || !form.fonte.trim()) {
      toast.error('Chave, valor e fonte são obrigatórios.');
      return;
    }
    // v1: valor numérico (dias) - mesmo padrão do seed (VALIDADE_APROVACAO_DIAS).
    const valorNumerico = Number(form.valor);
    const valor = Number.isNaN(valorNumerico) ? form.valor : valorNumerico;

    setSalvando(true);
    try {
      await parametrosInstituicaoApi.criarNovaVigencia({
        banco: form.banco,
        chave: form.chave.trim(),
        valor,
        fonte: form.fonte.trim(),
      });
      toast.success('Nova vigência criada.');
      setFormAberto(false);
      setForm((f) => ({ ...f, valor: '', fonte: '' }));
      await carregar();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro
        ?? (e instanceof Error ? e.message : 'Falha ao criar vigência.');
      toast.error(msg);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-xl font-semibold text-slate-800 mb-1">Parâmetros de Instituição</h1>
      <p className="text-sm text-slate-500 mb-5">
        Comportamento comercial do banco (ex.: régua de validade da aprovação de crédito) - família
        separada dos parâmetros normativos (governo). Valor vigente nunca é editado - toda mudança
        abre uma nova vigência (o registro anterior é preservado no histórico).
      </p>

      <div className="mb-4">
        <button
          onClick={() => setFormAberto(true)}
          className="text-sm px-3 py-2 bg-otm-orange text-white rounded-lg font-semibold hover:bg-otm-orange-dark transition-colors"
        >
          + Nova vigência
        </button>
      </div>

      {/* Fase 3 - form de nova vigência em MODAL LATERAL (padrão add/editar) */}
      <Sheet open={formAberto} onOpenChange={(aberto) => { if (!aberto) setFormAberto(false); }}>
        <SheetContent side="right" className="w-full sm:w-[42%] sm:max-w-none p-0 gap-0 flex flex-col bg-white">
          {/* Header */}
          <div className="shrink-0 px-6 pt-6 pb-4 border-b border-slate-100 pr-12 flex items-center gap-2.5">
            <span className="shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-otm-navy/10 text-otm-navy">
              <Building2 size={18} />
            </span>
            <div>
              <h3 className="text-base font-bold text-slate-900 leading-tight">Nova vigência</h3>
              <p className="text-xs text-slate-500 mt-0.5">Parâmetro de instituição - o valor anterior é preservado no histórico.</p>
            </div>
          </div>

          {/* Corpo */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
              <div>
                <label className={LABEL_CLS}>Banco<span className="text-red-500 ml-0.5">*</span></label>
                <select
                  value={form.banco}
                  onChange={(e) => setForm((f) => ({ ...f, banco: e.target.value as (typeof BANCOS)[number] }))}
                  className={INPUT_CLS}
                >
                  {BANCOS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL_CLS}>Chave<span className="text-red-500 ml-0.5">*</span></label>
                <input
                  type="text"
                  value={form.chave}
                  onChange={(e) => setForm((f) => ({ ...f, chave: e.target.value }))}
                  placeholder="VALIDADE_APROVACAO_DIAS"
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className={LABEL_CLS}>Valor (dias)<span className="text-red-500 ml-0.5">*</span></label>
                <input
                  type="number"
                  value={form.valor}
                  onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))}
                  placeholder="90"
                  className={INPUT_CLS}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={LABEL_CLS}>Fonte<span className="text-red-500 ml-0.5">*</span></label>
                <input
                  type="text"
                  value={form.fonte}
                  onChange={(e) => setForm((f) => ({ ...f, fonte: e.target.value }))}
                  placeholder="Ex.: Emerson, 14/07/2026 - campanha CAIXA jul/2026"
                  className={INPUT_CLS}
                />
                <p className="text-xs text-slate-400 mt-1">Quem decidiu e por quê (fica registrado junto da vigência).</p>
              </div>
            </div>
          </div>

          {/* Rodapé */}
          <div className="shrink-0 flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-white">
            <button
              type="button"
              onClick={() => setFormAberto(false)}
              className="text-sm font-medium text-slate-600 border border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={salvarNovaVigencia}
              disabled={salvando}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-otm-orange hover:bg-otm-orange-dark px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              <Save size={15} />{salvando ? 'Salvando…' : 'Salvar nova vigência'}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {erro && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">{erro}</div>
      )}

      {carregando ? (
        <div className="text-center text-slate-400 py-12">Carregando parâmetros…</div>
      ) : parametros.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">Nenhum parâmetro vigente.</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Banco</th>
                <th className="text-left px-4 py-3">Chave</th>
                <th className="text-left px-4 py-3">Valor</th>
                <th className="text-left px-4 py-3">Fonte</th>
                <th className="text-left px-4 py-3 w-36">Vigência desde</th>
                <th className="text-left px-4 py-3 w-24">Histórico</th>
              </tr>
            </thead>
            <tbody>
              {parametros.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-700">{p.banco}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{p.chave}</td>
                  <td className="px-4 py-3 text-slate-600" title={JSON.stringify(p.valor)}>{JSON.stringify(p.valor)}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs max-w-xs truncate" title={p.fonte}>{p.fonte}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{new Date(p.vigenciaInicio).toLocaleDateString('pt-BR')}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => abrirHistorico(p.banco, p.chave)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {historicoDe && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setHistoricoDe(null)}>
          <div className="bg-white rounded-xl p-5 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-800 text-sm">{historicoDe.banco} <span className="font-mono">{historicoDe.chave}</span></h2>
              <button onClick={() => setHistoricoDe(null)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <table className="w-full text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left py-2">Valor</th>
                  <th className="text-left py-2">Vigência início</th>
                  <th className="text-left py-2">Vigência fim</th>
                  <th className="text-left py-2">Fonte</th>
                </tr>
              </thead>
              <tbody>
                {historico.map((h) => (
                  <tr key={h.id} className="border-t border-slate-100">
                    <td className="py-2 max-w-xs truncate" title={JSON.stringify(h.valor)}>{JSON.stringify(h.valor)}</td>
                    <td className="py-2">{new Date(h.vigenciaInicio).toLocaleDateString('pt-BR')}</td>
                    <td className="py-2">{h.vigenciaFim ? new Date(h.vigenciaFim).toLocaleDateString('pt-BR') : <span className="text-green-600 font-medium">vigente</span>}</td>
                    <td className="py-2">{h.fonte}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
